import { useState } from 'react';
import { reportsApi, type DebtAgingDto } from '../../api/reports';
import MoneyCell from '../../components/MoneyCell';
import LoadingSpinner from '../../components/LoadingSpinner';

export default function DebtAging() {
  const [asOf, setAsOf] = useState(() => new Date().toISOString().slice(0, 10));
  const [type, setType] = useState('receivable');
  const [data, setData] = useState<DebtAgingDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await reportsApi.debtAging({ asOf, type });
      setData(result);
    } catch (e: any) {
      setError(e.message || 'Failed to generate aging report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <h1>Debt Aging Report</h1>

      <div className="filters" style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
        <label>
          As of: <input type="date" value={asOf} onChange={e => setAsOf(e.target.value)} />
        </label>
        <label>
          Type:
          <select value={type} onChange={e => setType(e.target.value)}>
            <option value="receivable">Receivables</option>
            <option value="payable">Payables</option>
          </select>
        </label>
        <button onClick={handleGenerate} disabled={loading}>
          {loading ? <LoadingSpinner /> : 'Generate'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {data && (
        <>
          {/* Summary Buckets */}
          <div className="card" style={{ marginBottom: '1rem' }}>
            <h2>Aging Summary</h2>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Bucket</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                  <th style={{ textAlign: 'right' }}>Persons</th>
                </tr>
              </thead>
              <tbody>
                {data.buckets.map((bucket, i) => (
                  <tr key={i}>
                    <td>{bucket.bucketName}</td>
                    <td style={{ textAlign: 'right' }}><MoneyCell value={bucket.amount} /></td>
                    <td style={{ textAlign: 'right' }}>{bucket.personCount}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td><strong>Total Outstanding</strong></td>
                  <td style={{ textAlign: 'right' }}><strong><MoneyCell value={data.totalOutstanding} /></strong></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Person Details */}
          <div className="card">
            <h2>Person Details</h2>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <th style={{ textAlign: 'right' }}>Current</th>
                  <th style={{ textAlign: 'right' }}>1-30d</th>
                  <th style={{ textAlign: 'right' }}>31-60d</th>
                  <th style={{ textAlign: 'right' }}>61-90d</th>
                  <th style={{ textAlign: 'right' }}>90+d</th>
                </tr>
              </thead>
              <tbody>
                {data.personDetails.map((person, i) => (
                  <tr key={i}>
                    <td>{person.person.name} ({person.person.code})</td>
                    <td style={{ textAlign: 'right' }}><MoneyCell value={person.totalOutstanding} /></td>
                    <td style={{ textAlign: 'right' }}><MoneyCell value={person.currentAmount} /></td>
                    <td style={{ textAlign: 'right' }}><MoneyCell value={person.bucket1to30} /></td>
                    <td style={{ textAlign: 'right' }}><MoneyCell value={person.bucket31to60} /></td>
                    <td style={{ textAlign: 'right' }}><MoneyCell value={person.bucket61to90} /></td>
                    <td style={{ textAlign: 'right' }}><MoneyCell value={person.bucketOver90} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
